/**
 * Copy deck for the map's Pin Key modal (`MapPinLegend`).
 *
 * Modelled on the legacy "Compatibility Key" from the old Ionic app
 * (`courseapp-client/src/assets/info.json`, `home` key), keeping the new app's
 * grouped structure (personal TasteMatch / unsure / listed) instead of the
 * legacy ten flat rows. Since 2026-10-04 (CD) there is no public-opinion or
 * yellow no-data pin: no personal TasteMatch means one grey "Unsure TasteMatch" pin.
 * Wording changes, and why:
 *
 * - "Personal Compatibility" became "Personal TasteMatch™" ("Personal
 *   TasteMatch™" is the sanctioned qualified usage, two words, never the
 *   fused, single-word trademarked form).
 * - The six level words (Highest ... No) are NOT stored here: they come from
 *   `RATING_EMOJIS`, the same source the profile TasteMatch modal uses, so the
 *   two keys always agree ("Highest", where the legacy app said "Most").
 * - The three explanatory paragraphs are the legacy ones reworded for
 *   TasteMatch; the legacy grey-pin paragraph (public opinion) is replaced
 *   because grey no longer carries a public-rating face.
 */
import { NOT_SURE_EMOJI_CHAR, tasteMatchBadgeCopy } from '@/copy/tasteMatchBadge';

export const mapPinKeyCopy = {
  /** Trigger label and modal title. */
  heading: 'Pin Key',
  /** Group 1: the six personal TasteMatch pins (green to red). */
  personal: {
    title: 'Personal TasteMatch™',
    description: 'Green to red, with a matching face.',
  },
  /**
   * Group 2: the grey "Unsure TasteMatch" pin (no personal TasteMatch). The 🤔
   * face is composited at runtime, see MapPinLegend. Title is the shared badge
   * label (no ™: the key's first TasteMatch mention, "Personal TasteMatch™",
   * already carries it).
   */
  unsure: {
    title: tasteMatchBadgeCopy.label,
    description: 'Grey. No TasteMatch for you here yet.',
  },
  /** Group 3: blue listed pin; half grey when there is no TasteMatch yet. */
  listed: {
    title: 'Listed',
    description: `Blue. On one of your lists. Half grey with a ${NOT_SURE_EMOJI_CHAR} face when there is no TasteMatch for you yet.`,
  },
  /** Explanatory paragraphs below the divider, in the legacy order. */
  explanation: [
    'Paire uses emojis of various expressions and corresponding pin colors to indicate your own personal TasteMatch with each restaurant on the map.',
    `A grey pin with a ${NOT_SURE_EMOJI_CHAR} is an Unsure TasteMatch: Paire doesn't have a personal TasteMatch for you at that restaurant yet.`,
    'As you build your PersonalTaste™ Profile (by taking more quizzes and privately rating more restaurants), Paire gets to know you better and changes the pins from grey to the color and emoji of your own personal TasteMatch.',
  ],
} as const;
